/**
 * <meditation-starfield> — canvas-based volumetric starfield for the player
 * surface. RNG-seeded stars at varying depths drift with parallax. Reads
 * the global `--audio-level` CSS custom property published by
 * meditation-audio every animation frame; brighter audio = more peak-spawns,
 * deeper twinkle, fatter glow halos.
 *
 * Cohesive with the existing brand palette: warm white + gold + cream
 * stars on the cosmic backdrop. The starfield sits behind every other
 * player chrome layer (z-index 0).
 */

import { getCanvasDpr, getFrameBudgetMs, getParticleScale, shouldRender } from "./perf-budget.ts";
import { getVoice } from "./frame-bus.ts";

const MAX_STARS = Math.max(20, Math.floor(220 * getParticleScale()));
const SEED_STARS = Math.max(15, Math.floor(130 * getParticleScale()));
const PEAK_THRESHOLD = 0.25;
const SPAWN_COOLDOWN_MS = 440;   // 220 → 440 (halve spawn rate)
const FRAME_BUDGET_MS = getFrameBudgetMs("atmosphere");

/** Halo sprite radius in CSS px. Every star's glow is the SAME radial
 *  gradient at a different scale and alpha, so we bake one sprite per hue
 *  at mount and blit it with drawImage instead of building a fresh
 *  CanvasGradient per star per frame.
 *
 *  The old path allocated one gradient object AND ran one large
 *  alpha-blended arc fill per star per frame under `lighter` compositing —
 *  pure GC churn plus fill-rate on the exact screen a phone is holding for
 *  twenty minutes straight. drawImage of a pre-rasterised sprite is a
 *  single blit the compositor is built for. */
const HALO_SPRITE_R = 32 as const;

type Star = {
  x: number;            // canvas px
  y: number;            // canvas px
  z: number;            // depth 0.05 (far) → 1 (near)
  vx: number;           // px / s
  vy: number;           // px / s
  baseSize: number;     // base radius before z-projection
  baseBri: number;      // 0.3 - 1
  twinklePhase: number; // 0 - 2π
  twinkleHz: number;    // 0.2 - 1.5
  hue: 0 | 1 | 2;       // 0=warm white, 1=gold, 2=cream
  spawnedAt: number;    // performance.now() — used for ephemeral lifecycle
  lifeMs: number;       // -1 = seed (infinite); positive = ephemeral
};

const HUE_RGB = [
  [245, 235, 215],  // warm white
  [255, 215, 100],  // gold
  [255, 230, 170],  // cream
] as const satisfies ReadonlyArray<readonly [number, number, number]>;

/** Bake one soft radial halo per hue. Built once, lazily, and shared by
 *  every starfield instance. Matches the old gradient stops exactly
 *  (1.0 → 0.4 at 40% → 0 at the rim) so the look is unchanged. */
let haloSprites: readonly HTMLCanvasElement[] | null = null;

const getHaloSprites = (): readonly HTMLCanvasElement[] => {
  if (haloSprites !== null) return haloSprites;
  const size = HALO_SPRITE_R * 2;
  haloSprites = HUE_RGB.map(([r, g, b]) => {
    const sprite = document.createElement("canvas");
    sprite.width = size;
    sprite.height = size;
    const sctx = sprite.getContext("2d");
    if (sctx !== null) {
      const grad = sctx.createRadialGradient(
        HALO_SPRITE_R, HALO_SPRITE_R, 0,
        HALO_SPRITE_R, HALO_SPRITE_R, HALO_SPRITE_R,
      );
      grad.addColorStop(0, `rgba(${r},${g},${b},0.95)`);
      grad.addColorStop(0.4, `rgba(${r},${g},${b},0.4)`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      sctx.fillStyle = grad;
      sctx.fillRect(0, 0, size, size);
    }
    return sprite;
  });
  return haloSprites;
};

const rand = (min: number, max: number): number => min + Math.random() * (max - min);
const choice = <T>(arr: ReadonlyArray<T>): T => {
  const idx = Math.floor(Math.random() * arr.length);
  return arr[idx] as T;  // safe: idx in bounds
};

class MeditationStarfield extends HTMLElement {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #stars: Star[] = [];
  #raf: number | null = null;
  #lastFrame: number = 0;
  #lastSpawn: number = 0;
  #dpr: number = 1;
  #w: number = 0;  // CSS px
  #h: number = 0;  // CSS px
  #resizeObserver: ResizeObserver | null = null;
  #visibilityHandler: (() => void) | null = null;

  connectedCallback(): void {
    // Low tier means "phone on battery", "reduced motion", or a weak
    // device — precisely Lisa's overheating case. The player already
    // carries its own atmosphere (medallion aurora, orbital motes, rim
    // bars); a second full-viewport canvas is the one layer here we can
    // drop without the room feeling empty. Mount nothing at all: no
    // canvas, no rAF, no resize observer.
    if (!shouldRender("starfield")) return;

    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;";
    this.style.position = this.style.position || "absolute";
    this.style.inset = "0";
    this.style.pointerEvents = "none";
    this.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext("2d");
    if (this.#ctx === null) return;

    this.#resize();
    this.#seed();

    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);

    // Pause when tab hidden — saves battery on phone with screen off.
    this.#visibilityHandler = (): void => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);

    this.#start();
  }

  disconnectedCallback(): void {
    this.#stop();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.#stars = [];
  }

  #resize(): void {
    if (this.#canvas === null || this.#ctx === null) return;
    const dpr = getCanvasDpr();
    const w = this.clientWidth || window.innerWidth;
    const h = this.clientHeight || window.innerHeight;
    this.#dpr = dpr;
    this.#w = w;
    this.#h = h;
    this.#canvas.width = Math.max(1, Math.floor(w * dpr));
    this.#canvas.height = Math.max(1, Math.floor(h * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #seedStar(): Star {
    return {
      x: Math.random() * this.#w,
      y: Math.random() * this.#h,
      z: rand(0.05, 1),
      vx: rand(-1.4, 1.4),
      vy: rand(-0.8, 0.8),
      baseSize: rand(0.3, 1),
      baseBri: rand(0.2, 0.6),
      twinklePhase: Math.random() * Math.PI * 2,
      twinkleHz: rand(0.15, 0.95),
      hue: choice([0, 0, 0, 0, 0, 0, 1, 2]),  // 6/8 warm, 1/8 gold, 1/8 cream
      spawnedAt: 0,
      lifeMs: -1,
    };
  }

  #seed(): void {
    this.#stars = [];
    for (let i = 0; i < SEED_STARS; i += 1) this.#stars.push(this.#seedStar());
  }

  #spawnPeakStar(level: number, now: number): void {
    if (this.#stars.length >= MAX_STARS) {
      // Cull the oldest ephemeral
      const oldestIdx = this.#stars.findIndex((s) => s.lifeMs > 0);
      if (oldestIdx >= 0) this.#stars.splice(oldestIdx, 1);
      else return;
    }
    // Spawn near the centre of the viewport so peaks "burst" outward.
    // Slight angular spread; let drift carry them past the focal area.
    const angle = Math.random() * Math.PI * 2;
    const r = rand(20, Math.min(this.#w, this.#h) * 0.18);
    const cx = this.#w / 2;
    const cy = this.#h / 2;
    const star = this.#seedStar();
    star.x = cx + Math.cos(angle) * r;
    star.y = cy + Math.sin(angle) * r;
    star.z = rand(0.55, 0.95);
    star.vx = Math.cos(angle) * rand(12, 36);
    star.vy = Math.sin(angle) * rand(12, 36);
    star.baseSize = rand(0.45, 1.1);
    star.baseBri = Math.min(0.7, level * 0.8 + 0.25);
    star.hue = Math.random() < 0.55 ? 2 : (Math.random() < 0.4 ? 1 : 0);
    star.spawnedAt = now;
    star.lifeMs = rand(2400, 4500);
    this.#stars.push(star);
  }

  /** Voice level from the in-memory frame bus. Was
   *  getComputedStyle(documentElement) — called twice per frame, each one a
   *  forced synchronous style recalculation of the whole document, while
   *  <meditation-audio> was writing the same property to :root from the
   *  other side. Read → write → read on every frame for twenty minutes. */
  #readAudioLevel(): number {
    return Math.min(1, Math.max(0, getVoice()));
  }

  #start(): void {
    if (this.#raf !== null) return;
    if (this.#canvas === null) return;
    this.#lastFrame = performance.now();
    const tick = (now: number): void => {
      const dt = now - this.#lastFrame;
      if (dt >= FRAME_BUDGET_MS) {
        this.#lastFrame = now;
        // Sample once per frame and thread it through — step() and
        // render() used to read it independently.
        const level = this.#readAudioLevel();
        this.#step(Math.min(50, dt), now, level);
        this.#render(now, level);
      }
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }

  #stop(): void {
    if (this.#raf !== null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }

  #step(dt: number, now: number, level: number): void {
    // Audio-reactive spawn — gentle bursts. 1 star per peak; occasional 2
    // when the level really pops above threshold.
    if (level >= PEAK_THRESHOLD && now - this.#lastSpawn >= SPAWN_COOLDOWN_MS) {
      const burst = level > PEAK_THRESHOLD + 0.2 ? 2 : 1;
      for (let i = 0; i < burst; i += 1) this.#spawnPeakStar(level, now);
      this.#lastSpawn = now;
    }

    const dts = dt / 1000;
    const w = this.#w;
    const h = this.#h;

    // Update positions; cull dead.
    for (let i = this.#stars.length - 1; i >= 0; i -= 1) {
      const s = this.#stars[i];
      if (!s) continue;
      // Lifecycle
      if (s.lifeMs > 0) {
        const age = now - s.spawnedAt;
        if (age >= s.lifeMs) {
          this.#stars.splice(i, 1);
          continue;
        }
      }
      // Parallax: closer stars drift more. Audio level adds gentle push outward.
      s.x += s.vx * s.z * dts;
      s.y += s.vy * s.z * dts;
      // Wrap seed stars; ephemeral stars pass off-screen and die naturally.
      if (s.lifeMs < 0) {
        if (s.x < -10) s.x = w + 10;
        else if (s.x > w + 10) s.x = -10;
        if (s.y < -10) s.y = h + 10;
        else if (s.y > h + 10) s.y = -10;
      }
    }
  }

  #render(now: number, level: number): void {
    if (this.#ctx === null) return;
    const ctx = this.#ctx;
    const sprites = getHaloSprites();
    ctx.clearRect(0, 0, this.#w, this.#h);
    ctx.globalCompositeOperation = "lighter";

    const tSec = now / 1000;
    for (const s of this.#stars) {
      // Twinkle 0-1
      const twinkle = (Math.sin(s.twinklePhase + tSec * s.twinkleHz * 2) + 1) / 2;
      // Reactive brightness: base + twinkle + moderate audio nudge.
      const reactive = (0.5 + 0.5 * twinkle) * s.baseBri * (0.6 + 0.4 * s.z);
      const audioBoost = level * s.baseBri * 0.32 * s.z;

      // Ephemeral fade-in / fade-out
      let lifeAlpha = 1;
      if (s.lifeMs > 0) {
        const age = now - s.spawnedAt;
        const t = age / s.lifeMs;
        // Triangular envelope: ramp in over first 12%, hold, fade last 30%.
        if (t < 0.12) lifeAlpha = t / 0.12;
        else if (t > 0.7) lifeAlpha = 1 - (t - 0.7) / 0.3;
      }

      const alpha = Math.min(1, (reactive + audioBoost) * lifeAlpha);
      if (alpha < 0.02) continue;

      const size = s.baseSize * (0.4 + s.z * 1.4);
      const rgb = HUE_RGB[s.hue] ?? HUE_RGB[0];
      const [r, g, b] = rgb;

      // Soft halo — blit the pre-baked sprite for this hue, scaled to the
      // star's reactive radius. Same gradient stops as before, zero
      // per-frame allocation.
      const haloR = size * (2.0 + level * 1.5 + s.z * 0.8);
      const sprite = sprites[s.hue] ?? sprites[0];
      if (sprite !== undefined) {
        ctx.globalAlpha = alpha;
        ctx.drawImage(sprite, s.x - haloR, s.y - haloR, haloR * 2, haloR * 2);
        ctx.globalAlpha = 1;
      }

      // Crisp core dot
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalCompositeOperation = "source-over";
  }
}

export const registerMeditationStarfield = (): void => {
  if (customElements.get("meditation-starfield") !== undefined) return;
  customElements.define("meditation-starfield", MeditationStarfield);
};
